import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2, UserMinus, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../components/EmptyState';
import { HelpHint } from '../components/HelpHint';
import { Tooltip } from '../components/Hover';
import { PageNav } from '../components/PageNav';
import { api } from '../lib/api';

/**
 * Groups of people who share the same extra jobs.
 *
 * A group grants nothing a direct grant could not — it is a way to say the same
 * thing about six people once. "Field and sales" is the real case: the four
 * technicians who also sign up neighbours, who would otherwise be four separate
 * grants that drift apart the moment somebody forgets one.
 *
 * Every rule that governs a direct grant governs membership too, checked on the
 * server: a group may not carry the administrator role, and adding somebody
 * with no technician roster entry to a group that grants the technician role is
 * refused with the reason on screen. A rule a group could route around would
 * not be a rule.
 */
export function AccessPage() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<{ name: string; description: string; roles: string[] }>({
    name: '',
    description: '',
    roles: [],
  });
  /** Which group's member list is open. */
  const [opened, setOpened] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const groups = useQuery({ queryKey: ['user-groups'], queryFn: api.userGroups });
  // The picker needs everybody, and this screen is admin-only anyway.
  const users = useQuery({ queryKey: ['users'], queryFn: api.users });

  const refresh = async () => {
    setError(null);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['user-groups'] }),
      queryClient.invalidateQueries({ queryKey: ['users'] }),
    ]);
  };

  const create = useMutation({
    mutationFn: () =>
      api.createGroup({
        name: form.name,
        description: form.description || undefined,
        roles: form.roles,
      }),
    onSuccess: async () => {
      setAdding(false);
      setForm({ name: '', description: '', roles: [] });
      await refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  const update = useMutation({
    mutationFn: (input: { id: string; body: Parameters<typeof api.updateGroup>[1] }) =>
      api.updateGroup(input.id, input.body),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteGroup(id),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });

  const addMember = useMutation({
    mutationFn: (input: { groupId: string; userId: string }) =>
      api.addGroupMember(input.groupId, input.userId),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });

  const removeMember = useMutation({
    mutationFn: (input: { groupId: string; userId: string }) =>
      api.removeGroupMember(input.groupId, input.userId),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });

  const grantable = groups.data?.grantable ?? [];
  const rows = groups.data?.groups ?? [];

  const toggleRole = (role: string) =>
    setForm((current) => ({
      ...current,
      roles: current.roles.includes(role)
        ? current.roles.filter((held) => held !== role)
        : [...current.roles, role],
    }));

  return (
    <div className="space-y-6">
      <PageNav crumbs={[{ label: 'Users', to: '/users' }, { label: 'Groups and roles' }]} />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-1.5 font-heading text-2xl">
            Groups and roles
            <HelpHint>
              Roles add up: somebody in a group that grants sales can do everything their own job
              allows <em>and</em> everything sales allows. What they can <strong>see</strong> does
              not change — a technician who also sells still sees only their own jobs on the board,
              because gaining a second job does not make you a dispatcher.
            </HelpHint>
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            A group says the same thing about several people at once. Put the technicians who also
            sign up customers in one group, and every one of them can work leads without you
            remembering four separate changes.
          </p>
        </div>

        {/*
          The one orange thing on this screen — and only ever one.

          Hidden while the page is empty, because the empty state below carries
          the same action and is already orange. Two "New group" buttons on one
          screen, both orange, is exactly the thing the theme forbids: the eye
          has nowhere to land, and neither button reads as *the* action. The
          empty state is the better of the two there, because it also says what
          a group is for.
        */}
        {(rows.length > 0 || adding) && (
          <button
            type="button"
            onClick={() => setAdding(!adding)}
            className={adding ? 'btn-secondary' : 'btn-primary'}
          >
            {!adding && <Plus className="h-4 w-4" aria-hidden="true" />}
            {adding ? 'Cancel' : 'New group'}
          </button>
        )}
      </div>

      {error && (
        <p className="rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          {error}
        </p>
      )}

      {adding && (
        <form
          className="card space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm">
              Name
              <input
                className="input mt-1"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                placeholder="Field and sales"
                required
                minLength={2}
                maxLength={60}
              />
            </label>
            <label className="text-sm">
              What it is for
              <input
                className="input mt-1"
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
                placeholder="Technicians who also sign up customers on site"
                maxLength={300}
              />
            </label>
          </div>

          <fieldset>
            <legend className="text-sm">What membership grants</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {grantable.map((role) => (
                <label
                  key={role}
                  className={`inline-flex min-h-tap cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm ${
                    form.roles.includes(role)
                      ? 'border-navy-700 bg-navy-50 text-navy-700'
                      : 'border-border bg-card text-muted-foreground'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={form.roles.includes(role)}
                    onChange={() => toggleRole(role)}
                  />
                  {role}
                </label>
              ))}
            </div>
            {/*
              Administrator is not on this list, and that is the point rather
              than an omission. It is said here because an admin will look for
              it and conclude the screen is broken.
            */}
            <p className="mt-2 text-xs text-muted-foreground">
              Administrator is not something a group can grant — it would hand somebody every
              permission in the system without the last-admin check ever running. Change a person’s
              own role instead.
            </p>
          </fieldset>

          {/* Orange while the form is open: this is what the screen is for now. */}
          <button
            type="submit"
            className="btn-primary"
            disabled={create.isPending || form.roles.length === 0 || form.name.trim().length < 2}
          >
            {create.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Create group
          </button>
        </form>
      )}

      {groups.isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading groups
        </p>
      )}

      {/*
        Not while the form is open. The empty state's whole job is to invite
        somebody to make the first group, and once the form is on screen that
        invitation has been accepted — leaving it there puts a second orange
        "New group" underneath the form's own action, which is exactly the
        two-orange breach this page has already been caught on twice.
      */}
      {groups.data && rows.length === 0 && !adding && (
        <EmptyState
          icon={Users}
          headline="No groups yet"
          body="A group is a way to give the same extra job to several people at once — the technicians who also sell, the two people who cover dispatch at weekends. Make one and add whoever belongs in it."
          action={{ label: 'New group', onClick: () => setAdding(true) }}
        />
      )}

      <div className="space-y-4">
        {rows.map((group) => {
          // Somebody already in it is not a candidate to add to it.
          const candidates = (users.data?.users ?? []).filter(
            (person) =>
              person.isActive &&
              !group.members.some((member) => member.user.id === person.id),
          );

          return (
            <div key={group.id} className={`card ${group.isActive ? '' : 'opacity-60'}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="font-heading text-lg">
                    {group.name}
                    {!group.isActive && (
                      <span className="badge ml-2 bg-muted text-muted-foreground">
                        switched off
                      </span>
                    )}
                  </h2>
                  {group.description && (
                    <p className="mt-0.5 text-sm text-muted-foreground">{group.description}</p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">grants</span>
                    {group.roles.map((role) => (
                      <span key={role} className="badge bg-navy-50 text-navy-700">
                        {role}
                      </span>
                    ))}
                    <span className="ml-2 text-xs text-muted-foreground">
                      {group.memberCount} {group.memberCount === 1 ? 'person' : 'people'}
                    </span>
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    className="btn-quiet text-xs"
                    onClick={() => setOpened(opened === group.id ? null : group.id)}
                    aria-expanded={opened === group.id}
                  >
                    {opened === group.id ? 'Hide people' : 'People'}
                  </button>

                  {/*
                    Suspending is the reversible one and is therefore the one
                    offered first. An off group keeps its member list and grants
                    nothing, which is almost always what somebody reaching for
                    "delete" actually wants.
                  */}
                  <Tooltip
                    label={
                      group.isActive
                        ? 'Keeps the list of people and grants nothing'
                        : 'Starts granting its roles again'
                    }
                    side="bottom"
                  >
                    <button
                      type="button"
                      className="btn-quiet text-xs"
                      disabled={update.isPending}
                      onClick={() =>
                        update.mutate({ id: group.id, body: { isActive: !group.isActive } })
                      }
                    >
                      {group.isActive ? 'Switch off' : 'Switch on'}
                    </button>
                  </Tooltip>

                  <Tooltip
                    label={
                      group.memberCount > 0
                        ? 'Take everybody out of it first — otherwise they lose access with nothing to explain why'
                        : 'Delete this group'
                    }
                    side="left"
                  >
                    <button
                      type="button"
                      className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
                      title={
                        group.memberCount > 0
                          ? `Take its ${group.memberCount} members out first — otherwise they lose access with nothing to explain why`
                          : `Delete ${group.name}`
                      }
                      disabled={remove.isPending || group.memberCount > 0}
                      onClick={() => remove.mutate(group.id)}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                      <span className="sr-only">Delete {group.name}</span>
                    </button>
                  </Tooltip>
                </div>
              </div>

              {opened === group.id && (
                <div className="mt-4 space-y-3 border-t border-border pt-4">
                  {group.members.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Nobody is in this group yet, so it grants nothing to anybody.
                    </p>
                  ) : (
                    <ul className="divide-y divide-border">
                      {group.members.map((member) => (
                        <li
                          key={member.user.id}
                          className="flex items-center justify-between gap-3 py-2"
                        >
                          <span className="min-w-0 text-sm">
                            <span className="font-medium">{member.user.name}</span>
                            <span className="block text-xs text-muted-foreground">
                              {member.user.role} · {member.user.email}
                            </span>
                          </span>
                          <button
                            type="button"
                            className="btn-quiet shrink-0 text-xs"
                            disabled={removeMember.isPending}
                            onClick={() =>
                              removeMember.mutate({ groupId: group.id, userId: member.user.id })
                            }
                          >
                            <UserMinus className="h-3.5 w-3.5" aria-hidden="true" />
                            Take out
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {candidates.length > 0 ? (
                    <label className="block text-sm">
                      Add somebody
                      <div className="mt-1 flex flex-wrap gap-2">
                        <select
                          className="input flex-1"
                          defaultValue=""
                          onChange={(event) => {
                            if (!event.target.value) return;
                            addMember.mutate({ groupId: group.id, userId: event.target.value });
                            event.target.value = '';
                          }}
                        >
                          <option value="">Choose a person</option>
                          {candidates.map((person) => (
                            <option key={person.id} value={person.id}>
                              {person.name} — {person.role}
                            </option>
                          ))}
                        </select>
                        {addMember.isPending && (
                          <span className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                            Adding
                          </span>
                        )}
                      </div>
                    </label>
                  ) : (
                    <p className="flex items-center gap-2 text-xs text-muted-foreground">
                      <UserPlus className="h-4 w-4" aria-hidden="true" />
                      Everybody who could be in this group already is.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="space-y-2 text-sm text-muted-foreground">
        <p>
          Changes here take effect on somebody’s next click — nobody has to sign out and back in.
          Taking a role away works the same way.
        </p>
        <p>
          Every change on this screen is written to the audit log with who made it and why, because
          “who gave the technician access to every customer’s details” is a question that gets asked
          six months later.
        </p>
      </div>
    </div>
  );
}
